/**
 * The vehicle card opened from a work order (#3400): the same VehicleDetailModal
 * the vehicle list and the create wizard use. The data is fetched only while the
 * card is open, and it is a modal, so the work order underneath keeps its tab
 * and unsaved edits. The footer's "create work order" is hidden because it
 * would navigate away from this one.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VehicleDetailModal } from '@/components/vehicles/VehicleDetailModal';
import { useVehicle, useVehicleHistory } from '@/hooks/useVehicles';
import { mapVehicleServiceHistory, toVehicleDetails } from './create/CustomerVehicleStep';

interface WorkOrderVehicleCardProps {
  vehicleId: string;
  open: boolean;
  onClose: () => void;
}

export function WorkOrderVehicleCard({ vehicleId, open, onClose }: WorkOrderVehicleCardProps) {
  const { t } = useTranslation('workorders');
  const { data: vehicleDetail } = useVehicle(open ? vehicleId : undefined);
  const { data: historyData } = useVehicleHistory(open ? vehicleId : undefined);
  const history = useMemo(
    () => mapVehicleServiceHistory(historyData?.data, t('noWorkOrderNumber')),
    [historyData, t],
  );
  const details = useMemo(
    () => (vehicleDetail ? toVehicleDetails(vehicleDetail, history) : null),
    [vehicleDetail, history],
  );

  if (!open || !details) return null;
  return (
    <VehicleDetailModal
      vehicle={details}
      serviceHistory={history}
      open
      onClose={onClose}
      hideCreateWorkOrder
    />
  );
}
